import { Container } from "pixi.js"
import { Entity, EntityId, ScreenDimensions } from "game-common/models"
import { ScalingParams } from "../../../client_util"
import { TranslationComputer } from "./translation_computer"
import { CameraFollow } from "./camera_follow"
import { ScreenShakeManager } from "./screen_shake_manager"
import { floorFallCameraOffset } from "./effects/floor_fall"

export interface CameraManagementServiceConfig {
    surface: Container
    worldOverlaySurface: Container
    screenShakeManager: ScreenShakeManager
    /** Facing of a held ranged attack by this entity, in radians, for the camera to lean toward. */
    aimAngle?: (entity: Entity) => number | undefined
}

export class CameraManagementService {
    private surface: Container
    private worldOverlaySurface: Container
    private screenShakeManager: ScreenShakeManager
    private translationComputer: TranslationComputer
    private follow = new CameraFollow()
    private followedId?: EntityId
    private aimAngle?: (entity: Entity) => number | undefined

    constructor(config: CameraManagementServiceConfig) {
        this.surface = config.surface
        this.worldOverlaySurface = config.worldOverlaySurface
        this.screenShakeManager = config.screenShakeManager
        this.aimAngle = config.aimAngle
        this.translationComputer = new TranslationComputer()
    }

    /**
     * Update the camera position based on player location
     */
    updatePlayerLocation = (entity: Entity, force?: boolean) => {
        if (!entity) {
            return
        }

        if (entity.location.x < 0 || entity.location.y < 0) {
            return
        }

        // Compute camera translation modifiers
        const fallOffset = entity.floorFall
            ? floorFallCameraOffset(entity, this.translationComputer.tilemapMetaProvider?.roomId) : undefined
        const focus = this.translationComputer.tilemapMetaProvider ? this.follow.focus(entity.location, Date.now(), {
            intent: entity.speed2,
            speed: entity.speed,
            aimAngle: this.aimAngle?.(entity),
            clamp: this.translationComputer.clampFocus,
            // A fall keeps its own camera, timed to the body (see TranslationComputer.compute).
            snap: force || !this.translationComputer.hasRunOnce || fallOffset !== undefined ||
                entity.entityId !== this.followedId,
        }) : entity.location
        this.followedId = entity.entityId
        this.translationComputer.compute(focus, force, fallOffset)
        const { modifierX, modifierY } = this.translationComputer

        // Apply screen shake and camera positioning
        const shakeState = this.screenShakeManager.getState()
        if (modifierX !== undefined) {
            const transformedX =
                (shakeState.shakeOffsetX + modifierX + ScreenDimensions.w / 2) * ScalingParams.surfaceZoom -
                48 * ScalingParams.viewportX
            this.surface.transform.position.x = transformedX
            this.worldOverlaySurface.transform.position.x = transformedX
        }
        if (modifierY !== undefined) {
            const transformedY =
                (shakeState.shakeOffsetY + modifierY + ScreenDimensions.h / 2) * ScalingParams.surfaceZoom -
                48 * ScalingParams.viewportY
            this.surface.transform.position.y = transformedY
            this.worldOverlaySurface.transform.position.y = transformedY
        }
    }

    /**
     * Trigger screen shake effect
     */
    screenShake = (entity: Entity) => {
        this.screenShakeManager.screenShake(entity, this.updatePlayerLocation)
    }

    /**
     * A decaying camera kick from a world event rather than from being hit. See
     * ScreenShakeManager.impulse for why this is a separate channel from screenShake.
     */
    screenShakeImpulse = (entity: Entity, amplitude: number, durationMs: number) => {
        this.screenShakeManager.impulse(entity, amplitude, durationMs, this.updatePlayerLocation)
    }

    /**
     * Update the translation computer with new tilemap data
     */
    updateTranslationComputer = (tilemapMetaProvider: any) => {
        this.translationComputer.update(tilemapMetaProvider)
    }

    /**
     * Reset camera state
     */
    reset = () => {
        this.translationComputer.reset()
        this.follow.reset()
    }

    /**
     * Get the current translation computer for advanced operations
     */
    getTranslationComputer = (): TranslationComputer => {
        return this.translationComputer
    }

    /**
     * Get the current screen shake manager
     */
    getScreenShakeManager = (): ScreenShakeManager => {
        return this.screenShakeManager
    }
}
